import React, { FC, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { OrderColumnItem } from "../../../interfaces/overview";
import { SortableItem } from "./SortableItem";

type Props = {
  columns: Array<OrderColumnItem>;
  setColumns: (item: OrderColumnItem[]) => void;
  updateOrderColumns: (columns: Array<OrderColumnItem>) => void;
};

const DragDropColumns: FC<Props> = ({
  columns,
  setColumns,
  updateOrderColumns,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = columns.findIndex((item) => item.key === active.id);
        const newIndex = columns.findIndex((item) => item.key === over.id);

        const newColumns = arrayMove(columns, oldIndex, newIndex);
        setColumns(newColumns);
        updateOrderColumns(newColumns);
      }
    },
    [columns, setColumns, updateOrderColumns],
  );

  const handleShowClick = useCallback(
    (key: string, show: boolean) => {
      const newColumns = columns.map((c) => ({
        ...c,
        show: c.key === key ? show : c.show,
      }));
      setColumns(newColumns);
      updateOrderColumns(newColumns);
    },
    [setColumns, columns, updateOrderColumns],
  );

  return (
    <div className="order-columns-popover__item">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((col) => col.key)}
          strategy={verticalListSortingStrategy}
          id="columns-sortable"
        >
          <div className="card__container">
            {columns.map((column) => (
              <SortableItem
                key={column.key}
                id={column.key}
                title={column.title}
                show={column.show}
                onShowClick={handleShowClick}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default DragDropColumns;
